import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

export function Hero({ children }: { children: React.ReactNode }) {
  return (
    <div className="relative overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-amber-50/30 via-emerald-50/20 to-purple-50/30" />
        <div className="absolute top-1/4 left-1/3 w-64 h-64 rounded-full bg-emerald-200/20 blur-3xl" />
        <div className="absolute bottom-1/4 right-1/3 w-80 h-80 rounded-full bg-amber-200/20 blur-3xl" />
        <div className="absolute top-1/2 right-1/4 w-72 h-72 rounded-full bg-purple-200/20 blur-3xl" />
      </div>

      <div className="container px-4 py-24 mx-auto md:py-32">
        <div className="flex flex-col items-center max-w-5xl mx-auto text-center">
          {children}
        </div>
      </div>
    </div>
  );
}

export function HeroBadges({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-center flex-wrap justify-center gap-2 mb-6">
      {children}
    </div>
  );
}

export function HeroBadge({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <Badge className={cn("px-3 py-1 text-sm", className)}>{children}</Badge>
  );
}

export function HeroTitle({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <h1
      className={cn(
        "mb-6 text-5xl font-bold tracking-tight text-zinc-900 md:text-6xl lg:text-7xl",
        className
      )}
    >
      {children}
    </h1>
  );
}

export function HeroDescription({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <p
      className={cn(
        "max-w-2xl mb-8 text-lg text-zinc-600 md:text-xl",
        className
      )}
    >
      {children}
    </p>
  );
}

export function HeroButtons({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "grid w-full max-w-md grid-cols-1 gap-4 sm:grid-cols-2",
        className
      )}
    >
      {children}
    </div>
  );
}

export function HeroCards({ children }: { children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-2 gap-4 mt-16 sm:grid-cols-4">
      {children}
    </div>
  );
}

export function HeroCard({ children }: { children: React.ReactNode }) {
  return (
    <div className="p-6 text-center rounded-lg bg-white/80 backdrop-blur-sm">
      {children}
    </div>
  );
}
